import { Renderer } from "@eplan/wgpurenderer/src/renderer.js"
import { Matrix4, Vector3 } from "@eplan/wgpurenderer/src/renderer.js";
import { GeometryGenerator } from "@eplan/wgpurenderer/util/geometryGenerator";
import { standardInit } from "@eplan/wgpurenderer/util/standardInit";
import { eplanInit } from "@eplan/wgpurenderer/util/eplanInit";
import * as Views from "@eplan/wgpurenderer/util/views";
import React, { useLayoutEffect, useEffect, useRef, useState } from "react";
import { createCabinet3D } from "./CustomCabinet3D";
import { createSections3D } from "./CustomSections3D";
import { buildColorMapWithProbing } from "../Utils/colorUtils";
import { buildObjectIdIndex } from "../Utils/nodeIndex";
import CubeFaceIcon from "../../../Common/CubeFaceIcon";
import api from "../../../../api/axios";
// Double-check this path against your real tree — see my note at the end.
import { ENCLOSURE_ID, selectionIds } from "../../../Layouts/LayoutContext";
import './PanelCanvasReal3D.css';

// NOTE: DeviceSectionTree used to be rendered inline, right of the canvas.
// It now lives in DevicesPanel instead, so this component can be dropped
// into an iframe / the center column on its own. Hide/select state is no
// longer owned here either — it comes in as hiddenIds/selectedId props
// (shared with the 2D canvas and the tree via LayoutContext) and this
// component's only job is to APPLY that shared state to the actual WebGPU
// scene nodes, and report clicks back up as plain ids via onSelect.

const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
export default function PanelCanvasReal3D({
  snapshot,
  version,
  optimized,
  sectionPlacements,
  sections,
  projectId,
  devices = [],
  onLoadingChange,
  onQuantityChange,
  onPlaceholderQuantityChange,
  onDeleteDevice,
  hiddenIds,   // NEW: Set<string> of hidden device/enclosure ids, shared across views
  selectedId,  // NEW: currently selected id, or null
  onSelect,    // NEW: (id | null) => void — call on canvas click
  onSceneReady, // ({ cabinetNodes, deviceNodeMap, onFit }) => void
}) {
  const canvasRef = useRef(null);

  // ---- Engine lifecycle refs -------------------------------------------
  const engineRef = useRef(null);
  const rendererRef = useRef(null);
  const rendererSetsRef = useRef(null);
  const allocatorRef = useRef(null);
  const cameraRef = useRef(null);
  const manipulatorRef = useRef(null);
  const materialsRef = useRef(null);
  const textureRef = useRef(null);
  const storeGeometryRef = useRef(null);
  const sceneRootNodeRef = useRef(null);
  const deviceNodeMapRef = useRef(new Map());
  const [deviceNodeMap, setDeviceNodeMap] = useState(new Map());
  const [cabinetNodes, setCabinetNodes] = useState(new Map());
  const {
    cabinet,
    ductLeft,
    ductRight,
    generatedSections,
    ductTop,
    ductTopEnclosure,
  } = snapshot;
  const [sceneRoot, setSceneRoot] = React.useState(null);
  const [showLegend, setShowLegend] = React.useState(false);
  const [hiddenNodes, setHiddenNodes] = React.useState(new Set());
  const [cabinetCadUrl, setCabinetCadUrl] = React.useState(null);
  const [deviceCadUrls, setDeviceCadUrls] = React.useState(new Map());
  const [deviceCadsLoaded, setDeviceCadsLoaded] = React.useState(false);
  const [engineReady, setEngineReady] = React.useState(false);
  const [cabinetInsideClearance, setCabinetInsideClearance] = React.useState(0);
  const objectIdIndexRef = useRef(new Map());
  const visiblePlacementIds = (snapshot.generatedSections || [])
    .map(s => s.sectionPlacementId)
    .filter(Boolean);

  const colorMap = buildColorMapWithProbing(visiblePlacementIds, {
    minHueDistance: 22,
    saturation: 65,
    lightness: 70,
  });

  const [selectedProject, setSelectedProject] = React.useState(null);

  const highlightedNodesRef = useRef([]);
  const highlightMaterialRef = useRef(null);
  const pointerDownRef = useRef(null);
  // Bridge from the engine's own click selection to our onSelect (see below)
  const handleEngineSelectRef = useRef(null);
  const [isSceneLoading, setIsSceneLoading] = React.useState(true);

  // FIXED: was `window.addEventListener("resize", ...)` only — that never
  // fires when a sibling panel is dragged wider/narrower or collapsed
  // (window size doesn't change, only this canvas's container does).
  // ResizeObserver watches the canvas's own box instead, so panel drags
  // and collapses actually resize the render target now.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;

    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      rendererRef.current?.resize?.();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    onLoadingChange?.(isSceneLoading);
  }, [isSceneLoading, onLoadingChange]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/project/${projectId}`)
      .then(res => setSelectedProject(res.data))
      .catch(err => {
        console.error("Failed to load project:", err);
      });
  }, [projectId]);

  useEffect(() => {
    const loadEnclosureData = async () => {
      const enclosureId = snapshot?.cabinet?.referenceId;
      if (!enclosureId) {
        setCabinetInsideClearance(0);
        return;
      }
      try {
        const response = await api.get(`/enclosure/${enclosureId}`);
        setCabinetInsideClearance(response.data?.insideClearance ?? 0);
      } catch (err) {
        console.error("Failed to load enclosure data:", err);
        setCabinetInsideClearance(0);
      }
    };
    loadEnclosureData();
  }, [snapshot?.cabinet?.referenceId]);

  useEffect(() => {
    const loadCad = async () => {
      const enclosureId = snapshot?.cabinet?.referenceId;
      if (!enclosureId) {
        setCabinetCadUrl(null);
        return;
      }
      try {
        const response = await api.get(`/enclosure/${enclosureId}/cad`, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);
        setCabinetCadUrl(blobUrl);
      } catch (err) {
        setCabinetCadUrl(null);
      }
    };
    loadCad();
  }, [snapshot?.cabinet?.referenceId]);

  useEffect(() => {
    const loadDeviceCads = async () => {
      setDeviceCadsLoaded(false);
      if (!snapshot?.generatedSections || !devices?.length) {
        setDeviceCadUrls(new Map());
        setDeviceCadsLoaded(true);
        return;
      }
      const masterDevById = new Map(devices.map(d => [d.id, d]));
      const subGroupIds = new Set();
      for (const section of snapshot.generatedSections) {
        for (const device of section.generatedDevices || []) {
          const master = device.deviceId ? masterDevById.get(device.deviceId) : null;
          const subGroupId = master?.subGroup?.id;
          if (subGroupId) subGroupIds.add(subGroupId);
        }
      }
      const entries = await Promise.all(
        [...subGroupIds].map(async (subGroupId) => {
          try {
            const response = await api.get(`/subgroup/${subGroupId}/cad`, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(response.data);
            return [subGroupId, blobUrl];
          } catch (err) {
            return [subGroupId, null];
          }
        })
      );
      setDeviceCadUrls(new Map(entries.filter(([, url]) => url !== null)));
      setDeviceCadsLoaded(true);
    };
    loadDeviceCads();
  }, [snapshot]);

  // EFFECT 1 — create the WebGPU engine exactly once per canvas.
  useLayoutEffect(() => {
    let mounted = true;
    if (!engineRef.current) {
      engineRef.current = eplanInit(canvasRef.current, true).then(
        ({ renderer, allocator, rendererSets, manipulator, camera }) => {
          rendererRef.current = renderer;
          rendererSetsRef.current = rendererSets;
          allocatorRef.current = allocator;
          cameraRef.current = camera;
          manipulatorRef.current = manipulator;

          // DEBUG - exposes the engine in the browser console so we can find
          // the renderer's pick/redraw methods. Remove once selection works.
          window.__wgpu = { renderer, rendererSets, manipulator, camera, allocator };

          // 3D -> tree: the engine picks on click by itself and calls these
          // rendererSets methods. Wrap them so we hear about it.
          ["select", "selectSet", "deselect", "deselectAll"].forEach((method) => {
            const original = rendererSets[method];
            if (typeof original !== "function") return;
            rendererSets[method] = function (...args) {
              // DEBUG - remove once selection works
              console.log(`[3D engine] rendererSets.${method}`, args);
              const result = original.apply(this, args);
              try {
                handleEngineSelectRef.current?.(method, args);
              } catch (err) {
                console.error("[3D pick] failed", err);
              }
              return result;
            };
          });

          const lightPalette = {
            back: [1, 1, 1],
            line: [0, 0, 0, 1],
            lineOnly: [0, 0.125, 0.25, 0.125],
            select: [1, 0, 0.25, 0.75],
            highlight: [0, 0.5, 1, 0.5],
            light: [1, 1, 1],
            highlightUi: [1, 1, 1, 0.75],
            ui: [0.25, 0, 0.125]
          };
          renderer.setPalette(lightPalette);
          rendererSets.setPalette(lightPalette);
          manipulator.setVisibleManipulators(["tX", "tY", "tZ"]);
          var materials = [
            { diffuse: [1, 0, 0.5, 1] },
            { diffuse: [0.5, 1, 0, 1] },
            { diffuse: [0, 0.5, 1, 1] },
            { diffuse: [1, 0.5, 0, 1] },
            { diffuse: [1, 0.75, 0.25, 1] },
            { diffuse: [0.7, 0.7, 0.7, 0.25] },
            { diffuse: [0, 0.6, 1, 1] },
            { diffuse: [0.82, 0.82, 0.80, 1] }
          ].map(m => {
            const materialIndex = allocator.getMaterialIndex();
            renderer.setMaterial(materialIndex, m);
            return materialIndex;
          });
          materialsRef.current = materials;
          highlightMaterialRef.current = materials[materials.length - 1];

          const diffuseTexture = new ImageData(new Uint8ClampedArray([
            255, 255, 255, 127,
            255, 0, 127, 127,
            127, 255, 0, 127,
            0, 127, 255, 127
          ]), 2);
          const normalTexture = new ImageData(new Uint8ClampedArray([
            179, 179, 232, 255,
            201, 201, 201, 255,
            127, 127, 255, 255,
            127, 127, 255, 255,
            127, 127, 255, 255,
            127, 127, 255, 255,
            53, 53, 201, 255,
            75, 75, 232, 255
          ]), 8);

          const texture = 0;
          renderer.storeTexture(texture, diffuseTexture, normalTexture);
          renderer.storeTexture(4, diffuseTexture, normalTexture);
          textureRef.current = texture;

          const remapMat = (obj, material, texture) => {
            var newObj = {};
            for (const type of Object.keys(obj)) {
              newObj[type] = obj[type].map(part => {
                var props = {};
                if (part.material != undefined)
                  props.material = { value: material ?? part.material };
                if (part.texture != undefined)
                  props.texture = { value: texture ?? part.texture };
                return Object.create(part, props);
              });
            }
            return newObj;
          };

          const storeGeometry = async (geometry, material, texture) => {
            const geometryIndex = allocator.getGeometryIndex();
            await renderer.storeGeometry(geometryIndex, remapMat(geometry, material, texture));
            return geometryIndex;
          };
          storeGeometryRef.current = storeGeometry;

          return renderer;
        }
      );
    }

    engineRef.current.then(() => {
      if (!mounted) return;
      setEngineReady(true);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EFFECT 2 — (re)build the scene whenever snapshot/version/CAD data change.
  useEffect(() => {
    if (!engineReady) return;
    if (!deviceCadsLoaded) return;
    if (!rendererRef.current || !allocatorRef.current || !storeGeometryRef.current) return;
    let cancelled = false;

    const buildScene = async () => {
      try {
        setIsSceneLoading(true);
        clearHighlights();
        const renderer = rendererRef.current;
        const allocator = allocatorRef.current;
        const storeGeometry = storeGeometryRef.current;
        const materials = materialsRef.current;
        const texture = textureRef.current;
        const camera = cameraRef.current;

        if (sceneRootNodeRef.current) {
          renderer.sceneRootNode.removeChild?.(sceneRootNodeRef.current);
          sceneRootNodeRef.current = null;
        }

        var scene = await createCabinet3D({
          renderer, allocator, storeGeometry, materials, texture, snapshot,
          selectedProject, cabinetCadUrl, insideClearance: cabinetInsideClearance,
        });

        if (!scene) {
          setIsSceneLoading(false);
          return;
        }

        setCabinetNodes({
          enclosure: scene.cabinetNode ? [scene.cabinetNode] : [],
          montagePlate: scene.montageNode ? [scene.montageNode] : [],
          panelsDoor: scene.cabinetNode ? [scene.cabinetNode] : [],
        });
        if (cancelled) return;

        renderer.sceneRootNode.addChild(scene.root);
        sceneRootNodeRef.current = scene.root;
        setSceneRoot(scene.root);

        deviceNodeMapRef.current.clear();
        await withTimeout(createSections3D({
          renderer, allocator, storeGeometry, materials, snapshot, texture,
          parent: scene.montageInternalNode,
          sectionPlacements, sections,
          showSectionBorders: showLegend,
          devices, colorMap, selectedProject, deviceCadUrls,
          insideClearance: cabinetInsideClearance,
          deviceNodeMap: deviceNodeMapRef.current,
        }), 15000, "createSections3D");
        console.log("[3D] sections done");

        // Built AFTER the devices were added, so device nodes are indexed too
        // (needed later for 3D -> tree picking).
        objectIdIndexRef.current = buildObjectIdIndex(scene.root);

        setDeviceNodeMap(new Map(deviceNodeMapRef.current));
        if (cancelled) return;

        await withTimeout(camera.zoomToVisible(2), 5000, "zoomToVisible");
        setIsSceneLoading(false);
      } catch (error) {
        console.error("Scene build failed:", error);
      } finally {
        if (!cancelled) setIsSceneLoading(false);
      }

    };

    buildScene();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineReady, snapshot, version, cabinetCadUrl, cabinetInsideClearance, deviceCadUrls, deviceCadsLoaded, selectedProject]);

  // ---- Highlight / visibility, driven by shared props -------------------
  const clearHighlights = () => {
    highlightedNodesRef.current.forEach(node => { node.colorOverride = [0, 0, 0, 0]; });
    highlightedNodesRef.current = [];
  };

  const highlightNodeRecursive = (node) => {
    if (!node) return;
    node.colorOverride = [0, 0.6, 1, 1];
    highlightedNodesRef.current.push(node);
    node.children?.forEach(highlightNodeRecursive);
  };

  const setVisible = (nodes, visible) => {
    const list = Array.isArray(nodes) ? nodes : [nodes];
    list.forEach(node => {
      if (!node) return;
      node.visible = visible;
      node.children?.forEach(child => setVisible(child, visible));
    });
  };

  // Ask the renderer to draw a new frame after node properties changed.
  // TEMPORARY: tries common method names until we know the real one;
  // resize() is a known method and also forces a redraw.
  const requestRedraw = () => {
    const r = rendererRef.current;
    if (!r) return;
    try {
      if (typeof r.requestRender === "function") r.requestRender();
      else if (typeof r.redraw === "function") r.redraw();
      else if (typeof r.invalidate === "function") r.invalidate();
      else r.resize?.();
    } catch (err) {
      console.warn("[3D] redraw failed", err);
    }
  };

  // Apply hiddenIds (shared with 2D canvas + tree) to the real scene nodes.
  useEffect(() => {
    if (!hiddenIds) return;
    deviceNodeMap.forEach((nodes, subGroupId) => {
      setVisible(nodes, !hiddenIds.has(subGroupId));
    });
    cabinetNodes?.enclosure?.forEach(n => setVisible(n, !hiddenIds.has(ENCLOSURE_ID)));
    requestRedraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenIds, deviceNodeMap, cabinetNodes]);

  // Apply selectedId (shared) as a highlight: enclosure, one device,
  // or a whole section ("section:<id>") -> every device of it.
  useEffect(() => {
    clearHighlights();

    if (!selectedId) {
      requestRedraw();
      return;
    }

    let nodesFound = 0;
    let ids = [];

    if (selectedId === ENCLOSURE_ID) {
      const nodes = cabinetNodes?.enclosure || [];
      nodesFound = nodes.length;
      nodes.forEach(highlightNodeRecursive);
    } else {
      ids = selectionIds(selectedId, sectionPlacements) ?? [];
      ids.forEach((id) => {
        const nodes = deviceNodeMap.get(id) || [];
        nodesFound += nodes.length;
        nodes.forEach(highlightNodeRecursive);
      });
    }

    // DEBUG - remove once selection works
    console.log("[3D select]", {
      selectedId,
      ids,
      nodesFound,
      mapKeys: [...deviceNodeMap.keys()],
    });

    requestRedraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, deviceNodeMap, cabinetNodes, sectionPlacements]);

  // ---- 3D -> tree: reverse index (scene node -> selection id) ----------
  // Every node of a device (and all its children, e.g. CAD sub-meshes)
  // maps to the id the tree and 2D canvas use (subGroup.id), the enclosure
  // to ENCLOSURE_ID. Devices first, so they win if a node were ever shared.
  const nodeToSelectionIdRef = useRef(new Map());

  const indexSelectable = (node, id, map) => {
    if (!node || map.has(node)) return;
    map.set(node, id);
    node.children?.forEach(child => indexSelectable(child, id, map));
  };

  useEffect(() => {
    const map = new Map();
    deviceNodeMap.forEach((nodes, id) => nodes.forEach(n => indexSelectable(n, id, map)));
    (cabinetNodes?.enclosure || []).forEach(n => indexSelectable(n, ENCLOSURE_ID, map));
    nodeToSelectionIdRef.current = map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceNodeMap, cabinetNodes]);

  // ---- Camera ------------------------------------------------------------
  const handleFitToScreen = async () => {
    try {
      await cameraRef.current?.zoomToVisible(2);
    } catch (err) {
      console.error("Fit to screen failed:", err);
    }
  };

  // ---- 3D -> tree: selection made by the engine -------------------------
  // The renderer has no pick method we can call. Instead, eplanInit already
  // handles clicks itself: it picks the node under the mouse and calls
  // rendererSets.select(...) / deselectAll() (that's the
  // "State: showManipulators" log). We wrap those methods (see EFFECT 1)
  // and translate the engine's selection into our shared selection id.

  // Walk up from the clicked (sub-)mesh until we reach a device/enclosure node.
  const resolveSelectionId = (node) => {
    for (let n = node; n; n = n.parent) {
      const id = nodeToSelectionIdRef.current.get(n);
      if (id) return id;
    }
    return null;
  };

  // The engine may pass a node, a node id, an array or a Set - find the node.
  const findNodeInArgs = (value, depth = 0) => {
    if (value == null || depth > 3) return null;
    if (typeof value === "number") return objectIdIndexRef.current.get(value) ?? null;
    if (typeof value === "object") {
      if ("parent" in value || "children" in value) return value; // scene node
      if (typeof value[Symbol.iterator] === "function") {
        for (const item of value) {
          const node = findNodeInArgs(item, depth + 1);
          if (node) return node;
        }
      }
    }
    return null;
  };

  // Called by the wrapped rendererSets methods. Re-assigned on every render
  // so it always sees the latest onSelect / indexes.
  const deselectTimerRef = useRef(null);
  handleEngineSelectRef.current = (method, args) => {
    if (method === "deselectAll" || method === "deselect") {
      // A click on a device usually does deselectAll() + select(node) right
      // after each other. Only report "nothing selected" if no select follows.
      clearTimeout(deselectTimerRef.current);
      deselectTimerRef.current = setTimeout(() => onSelect?.(null), 0);
      return;
    }

    if (method === "select" || method === "selectSet") {
      clearTimeout(deselectTimerRef.current);
      const node = findNodeInArgs(args);
      const id = node ? resolveSelectionId(node) : null;

      // DEBUG - remove once selection works
      console.log("[3D pick]", { method, nodeId: node?.id, nodeName: node?.name, id });

      if (id) onSelect?.(id);
    }
  };

  useEffect(() => () => clearTimeout(deselectTimerRef.current), []);

  // ---- Fallback: ask the engine what is selected after a click ----------
  // The engine calls its selection internally (our wrappers above may never
  // run). So after every real click we ask rendererSets.anySelected(...)
  // for each device's nodes, and report the first device that is selected.
  const isSelectedInEngine = (nodes) => {
    const sets = rendererSetsRef.current;
    if (!sets || typeof sets.anySelected !== "function") return false;
    try {
      if (sets.anySelected(nodes)) return true;   // signature: (nodes[])
    } catch { /* try per node */ }
    for (const node of nodes) {
      try {
        if (sets.anySelected(node)) return true;  // signature: (node)
      } catch { /* ignore */ }
    }
    return false;
  };

  const debugLoggedRef = useRef(false);

  const syncSelectionFromEngine = () => {
    const sets = rendererSetsRef.current;

    // DEBUG (once) - prints the engine's own code for these methods, so we
    // can see their real signatures if this fallback doesn't work either.
    if (!debugLoggedRef.current && sets) {
      debugLoggedRef.current = true;
      const src = (fn) => (typeof fn === "function" ? String(fn).slice(0, 600) : String(fn));
      console.log("[3D debug] rendererSets.anySelected:\n", src(sets.anySelected));
      console.log("[3D debug] rendererSets.select:\n", src(sets.select));
      console.log("[3D debug] manipulator.syncToSelection:\n", src(manipulatorRef.current?.syncToSelection));
    }

    let found = null;
    for (const [id, nodes] of deviceNodeMap) {
      if (isSelectedInEngine(nodes)) { found = id; break; }
    }
    if (!found && isSelectedInEngine(cabinetNodes?.enclosure || [])) {
      found = ENCLOSURE_ID;
    }

    // DEBUG - remove once selection works
    console.log("[3D click]", { found, devicesChecked: deviceNodeMap.size });

    onSelect?.(found); // device / enclosure id, or null for empty space
  };

  // Latest version for the native listeners below (avoids stale closures)
  const syncSelectionRef = useRef(null);
  syncSelectionRef.current = syncSelectionFromEngine;

  // Native listeners in the CAPTURE phase, so they run even if the engine's
  // own handlers stop the event from reaching React.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onDown = (e) => {
      pointerDownRef.current = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e) => {
      const down = pointerDownRef.current;
      pointerDownRef.current = null;
      // Ignore rotate/pan drags
      if (!down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 4) return;
      // Give the engine time to process the click and update its selection
      setTimeout(() => syncSelectionRef.current?.(), 50);
    };

    canvas.addEventListener("pointerdown", onDown, true);
    canvas.addEventListener("pointerup", onUp, true);
    return () => {
      canvas.removeEventListener("pointerdown", onDown, true);
      canvas.removeEventListener("pointerup", onUp, true);
    };
  }, []);

  const handleChangeView = async (face) => {
    try {
      const camera = cameraRef.current;

      if (!camera) return;

      await camera.setView(
        Views.predefViews[face],
        0.25
      );
    } catch (err) {
      console.error("Failed to change view:", err);
    }
  };

  // Hand the 3D-specific data up (needed to apply hiddenIds/selectedId
  // above) plus onFit for the toolbar.
  useEffect(() => {
    onSceneReady?.({ cabinetNodes, deviceNodeMap, onFit: handleFitToScreen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinetNodes, deviceNodeMap]);

  return (
    <div className="canvas3d-container">
      <canvas
        ref={canvasRef}
        id="canvas3d"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Escape") onSelect?.(null); }}
        style={{ width: "100%", height: "100%", display: "block", background: "white" }}
      />

      <div className="cube-face-toolbar">
        <button
          onClick={handleFitToScreen}
          title="Fit to screen"
          className="fit-button"
        >
          ⤢
        </button>
        {["top", "bottom", "front", "back", "left", "right"].map((f) => (
          <button
            key={f}
            title={f}
            className="cube-face-button"
            onClick={() => handleChangeView(f)}
          >
            <CubeFaceIcon face={f} />
          </button>
        ))}
      </div>
    </div>
  );
}
